import { NextRequest, NextResponse } from 'next/server';

import { getCookieName, getStage } from '@toolkit/http-api/auth/cookies';
import type { CookieName } from '@toolkit/http-api/auth/cookies';
import type { Tokens } from '@website/utils/oauthClient';

// Re-export from toolkit for consistent cookie name handling across packages
export { getCookieName, getStage };
export type { CookieName };

/**
 * Get the cookie domain from a hostname.
 * This is the core logic used by both server-side (via NEXT_PUBLIC_WEBSITE_URL)
 * and client-side (via window.location.hostname) code.
 *
 * With stage-prefixed cookie names, we can use broader domain scopes
 * since cookies won't collide between environments.
 *
 * Examples:
 * - jordane.dev.toptoptime.com → .dev.toptoptime.com
 * - dev.toptoptime.com → .dev.toptoptime.com
 * - gamma.toptoptime.com → .gamma.toptoptime.com
 * - toptoptime.com → .toptoptime.com
 * - localhost → undefined (no domain restriction)
 */
export function getCookieDomainFromHostname(hostname: string): string | undefined {
  // No domain for localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return undefined;
  }

  // Personal dev stages and dev itself: *.dev.toptoptime.com → .dev.toptoptime.com
  // Cookie names are stage-prefixed so they don't collide
  if (hostname.endsWith('.dev.toptoptime.com') || hostname === 'dev.toptoptime.com') {
    return '.dev.toptoptime.com';
  }

  // Gamma environment
  if (hostname === 'gamma.toptoptime.com' || hostname.endsWith('.gamma.toptoptime.com')) {
    return '.gamma.toptoptime.com';
  }

  // Production
  if (hostname === 'toptoptime.com' || hostname.endsWith('.toptoptime.com')) {
    return '.toptoptime.com';
  }

  // Fallback: no domain restriction
  return undefined;
}

/**
 * Get the cookie domain for cross-subdomain authentication.
 * This allows cookies set on the website to be accessible by the API.
 * Uses NEXT_PUBLIC_WEBSITE_URL to determine the hostname.
 */
export function getCookieDomain(): string | undefined {
  const websiteUrl = process.env.NEXT_PUBLIC_WEBSITE_URL;
  if (!websiteUrl) return undefined;

  try {
    const url = new URL(websiteUrl);
    return getCookieDomainFromHostname(url.hostname);
  } catch {
    return undefined;
  }
}

/**
 * Get cookie options with domain for cross-subdomain auth
 */
export function getCookieOptions() {
  return {
    httpOnly: true,
    sameSite: 'lax' as const,
    secure: process.env.NODE_ENV === 'production',
    path: '/',
    domain: getCookieDomain()
  };
}

// Legacy export for backward compatibility
export const cookieOptions = {
  httpOnly: true,
  sameSite: 'lax' as const,
  secure: process.env.NODE_ENV === 'production',
  path: '/'
} as const;

const REFRESH_TOKEN_MAX_AGE = 60 * 60 * 24 * 90; // 90 days

export function setAuthCookies(response: NextResponse, tokens: Tokens) {
  const options = getCookieOptions();
  response.cookies.set(getCookieName('access_token'), tokens.access, {
    ...options,
    maxAge: tokens.expiresIn
  });
  if (tokens.refresh) {
    response.cookies.set(getCookieName('refresh_token'), tokens.refresh, {
      ...options,
      maxAge: REFRESH_TOKEN_MAX_AGE
    });
  }
}

export function getAuthCookies(request: NextRequest) {
  const accessTokenName = getCookieName('access_token');
  const refreshTokenName = getCookieName('refresh_token');

  return request.cookies.has(accessTokenName)
    ? {
        access_token: request.cookies.get(accessTokenName),
        refresh_token: request.cookies.get(refreshTokenName)
      }
    : {};
}

export function clearAuthCookies(response: NextResponse) {
  // To properly delete cookies, set them with maxAge: 0 and expired date
  // IMPORTANT: Cookie attributes must match how they were originally set
  const domain = getCookieDomain();

  // Clear httpOnly cookies (access_token, refresh_token)
  const clearHttpOnlyCookies = {
    httpOnly: true,
    sameSite: 'lax' as const,
    secure: process.env.NODE_ENV === 'production',
    path: '/',
    domain,
    maxAge: 0,
    expires: new Date(0)
  };

  // Clear non-httpOnly cookie (user_type)
  // CRITICAL: user_type is set WITHOUT httpOnly in callback/route.ts and impersonate/route.ts
  const clearUserTypeCookie = {
    httpOnly: false, // Must match how it was set
    sameSite: 'lax' as const,
    secure: process.env.NODE_ENV === 'production',
    path: '/',
    domain,
    maxAge: 0,
    expires: new Date(0)
  };

  response.cookies.set(getCookieName('access_token'), '', clearHttpOnlyCookies);
  response.cookies.set(getCookieName('refresh_token'), '', clearHttpOnlyCookies);
  response.cookies.set(getCookieName('user_type'), '', clearUserTypeCookie);
}
